import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, FileText, X } from "lucide-react";
import { toast } from "sonner";

const HIDE_ON = ["/terms", "/privacy", "/auth", "/forgot-password", "/reset-password", "/activate"];

// Non-blocking soft notice for existing users when a NEW, non-mandatory Terms
// version is available. It never blocks access. Dismissing ("Maybe later") is NOT
// acceptance — acceptance is recorded ONLY when the user explicitly accepts.
export function TermsUpdateNotice() {
  const { user } = useAuth();
  const location = useLocation();
  const [info, setInfo] = useState(null);   // { optional_acceptance, terms_version, optional_summary }
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const hidden = HIDE_ON.some((p) => location.pathname.startsWith(p));
  const dismissKey = user ? `nf_terms_notice_dismissed_${user.id}` : null;

  useEffect(() => {
    if (!user || hidden) { setInfo(null); return; }
    api.get("/policies/current")
      .then((r) => setInfo(r.data))
      .catch(() => setInfo(null));
  }, [user, location.pathname, hidden]);

  if (!user || hidden || !info?.optional_acceptance) return null;
  const dismissed = dismissKey ? sessionStorage.getItem(dismissKey) === (info.terms_version || "1") : false;
  if (dismissed) return null;

  const dismiss = () => { try { if (dismissKey) sessionStorage.setItem(dismissKey, info.terms_version || "1"); } catch { /* */ } setInfo(null); };

  const accept = async () => {
    setSaving(true);
    try {
      await api.post("/policies/accept");
      setOpen(false);
      setInfo(null);
      toast.success("Thanks — you've accepted the updated Terms");
    } catch {
      toast.error("Couldn't record your acceptance. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <>
      <div data-testid="terms-update-notice"
        className="fixed inset-x-0 bottom-3 z-[55] mx-auto flex w-[calc(100%-1.5rem)] max-w-xl items-start gap-3 rounded-2xl border border-emerald-200 bg-white/95 p-4 shadow-xl backdrop-blur-xl sm:bottom-5">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white"><FileText className="h-4 w-4" /></div>
        <div className="min-w-0 flex-1">
          <p className="font-display text-sm font-bold text-zinc-900">We've updated our Terms (v{info.terms_version})</p>
          <p className="mt-0.5 text-xs text-zinc-600">New Section 7 explains collaboration content ownership and a default organic social-sharing permission. Reviewing and accepting is optional — you can keep using Nearfluence either way.</p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <Button size="sm" data-testid="terms-notice-review" onClick={() => setOpen(true)} className="rounded-full bg-emerald-600 hover:bg-emerald-700">Review &amp; Accept</Button>
            <Button size="sm" variant="ghost" data-testid="terms-notice-later" onClick={dismiss} className="rounded-full text-zinc-500">Maybe later</Button>
          </div>
        </div>
        <button data-testid="terms-notice-close" onClick={dismiss} className="shrink-0 rounded-full p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"><X className="h-4 w-4" /></button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="terms-notice-modal" className="max-h-[85vh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Updated Terms — v{info.terms_version}</DialogTitle>
            <DialogDescription>Please review the summary of what's changed. Accepting is optional.</DialogDescription>
          </DialogHeader>
          <p className="text-sm leading-relaxed text-zinc-700">{info.optional_summary}</p>
          <div className="mt-3 rounded-xl bg-emerald-50/70 p-3 text-xs text-zinc-600">
            The new default content-sharing permission (Section 7) will apply only to <span className="font-semibold">future collaborations</span> after you accept these Terms. It does not change any existing or past collaboration, and you can always agree different arrangements directly with a business.
          </div>
          <div className="mt-3 text-sm">
            <Link to="/terms" target="_blank" data-testid="terms-notice-view-full" className="font-semibold text-emerald-700 hover:underline">Read the full Terms of Service</Link>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="terms-notice-accept" onClick={accept} disabled={saving} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept updated Terms"}
            </Button>
            <Button variant="outline" data-testid="terms-notice-modal-later" onClick={() => { setOpen(false); dismiss(); }} className="rounded-full">Maybe later</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
